/* eslint-disable no-nested-ternary */
import {
  Alert,
  Box,
  Button,
  Card,
  CardMedia,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Paper,
  Container,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import React, { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Link, useParams } from 'react-router-dom';
import { dropdownKeyEventHandler } from 'renderer/shared/callbacks/keyEventHandler';
import {
  useCreateProductMutation,
  useEditProductMutation,
  useGetProductDetailsQuery,
} from '../services/products.endpoints';
import {
  CreateProductRequest,
  ProductFormSchema,
  SupportedHostApps,
} from '../schemas/products.schemas';
import {
  platformOptions,
  productCategoryOptions,
  productIdOptions,
} from '../constants/productDropdowns';
import productThumbnails from '../constants/productThumbnails';
import AssignSupportedHostApps from './AssignSupportedHostApps';
import MarkdownEditor from './MarkdownEditor';

interface ProductForm {
  name: string;
  category: string;
  priceId?: string;
  upgradeId?: string;
  downgradeId?: string;
  productId: CreateProductRequest['productId'] | '';
  addOnId?: string;
  // subscriptionPlans: SubscriptionPlan[];
  fullPrice?: number | string;
  hidden?: boolean;
  keywords: string;
  sale?: {
    salePrice: number | '';
    until: string | null;
  };
  briefDescription: string;
  productPage: string;
  downloadPage: string;
  supportedPlatforms: string[];
  supportedHostApplications: {
    displayName: string;
    id: string;
    version: string;
  }[];
  fullDescription: string;
}

const initialProduct: ProductForm = {
  name: '',
  category: '',
  productId: '',
  addOnId: '',
  priceId: '',
  upgradeId: '',
  downgradeId: '',
  // subscriptionPlans: [],
  fullPrice: '',
  hidden: false,
  keywords: '',
  briefDescription: '',
  productPage: '',
  downloadPage: '',
  supportedPlatforms: ['Macintosh', 'Windows'],
  supportedHostApplications: [],
  fullDescription: '',
};

interface ProductFormProps {
  mode: 'edit' | 'create';
}

const ProductFormPage = ({ mode }: ProductFormProps) => {
  const { id } = useParams<{ id: string }>();
  const [
    createProduct,
    {
      isLoading: createIsLoading,
      error: createError,
      isSuccess: createSuccess,
    },
  ] = useCreateProductMutation();
  const [
    editProduct,
    { isLoading: editIsLoading, error: editError, isSuccess: editSuccess },
  ] = useEditProductMutation();
  const {
    data: productDetails,
    isFetching: detailsAreLoading,
    error: detailsError,
  } = useGetProductDetailsQuery(id ?? '');
  const [productForm, setProductForm] = useState(initialProduct);
  const [formError, setFormError] = useState(false);

  // Load product details into the edit form
  useEffect(() => {
    if (mode === 'edit' && productDetails) {
      const updatedProductDetails = {
        ...productDetails,
      };
      setProductForm(updatedProductDetails);
    }
  }, [mode, productDetails]);

  useEffect(() => {
    if (mode === 'create' && createSuccess) {
      setProductForm(initialProduct);
    }
  }, [createSuccess, mode]);

  const isLoading =
    mode === 'create' ? createIsLoading : editIsLoading || detailsAreLoading;
  const error = mode === 'create' ? createError : editError || detailsError;
  const isSuccess = mode === 'create' ? createSuccess : editSuccess;

  const {
    name,
    category,
    keywords,
    hidden,
    productId,
    priceId,
    upgradeId,
    downgradeId,
    fullPrice,
    sale,
    briefDescription,
    productPage,
    downloadPage,
    supportedPlatforms,
    supportedHostApplications,
    fullDescription,
  } = productForm;

  const { salePrice, until } = sale || {};

  const submitButtonTextOptions = {
    edit: 'Edit Product',
    create: 'Create Product',
  };
  const submitButtonText = submitButtonTextOptions[mode];

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProductForm({ ...productForm, [event.target.name]: event.target.value });
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    try {
      const validated = ProductFormSchema.parse(productForm);
      if (validated.fullPrice === '') {
        delete validated.fullPrice;
      }
      if (mode === 'create') {
        createProduct(validated);
      } else {
        editProduct({ body: validated, id: id ?? '' });
      }
      setFormError(false);
    } catch (err) {
      setFormError(true);
    }
  };

  const clearSale = () => {
    setProductForm({
      ...productForm,
      sale: undefined,
    });
  };

  const editSale = (newSale: ProductForm['sale']) => {
    setProductForm({
      ...productForm,
      sale: {
        salePrice: newSale?.salePrice ?? '',
        until: newSale?.until ?? null,
      },
    });
  };

  return (
    <Container maxWidth="lg">
      <Box display="flex">
        <Box flex={1} p={3}>
          <Paper style={{ padding: '20px', backgroundColor: '#fff' }}>
            <form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Link to={mode === 'create' ? '/' : `/products/${id ?? ''}`}>
                    <Button startIcon={<ArrowBackIcon />}>
                      {mode === 'create'
                        ? 'Back to all products'
                        : 'Back to product details'}
                    </Button>
                  </Link>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h4">Add a New Product</Typography>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    id="productId"
                    label="Image"
                    name="productId"
                    required
                    fullWidth
                    select
                    disabled={isLoading}
                    value={productId}
                    onChange={handleChange}
                  >
                    {productIdOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        <Card>
                          <CardMedia
                            component="img"
                            image={
                              productThumbnails.get(
                                value as CreateProductRequest['productId'],
                              ) ?? ''
                            }
                            alt={name}
                          />
                        </Card>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={7} container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      id="name"
                      label="Product Name"
                      name="name"
                      required
                      fullWidth
                      disabled={isLoading}
                      value={name}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id="category"
                      label="Category"
                      name="category"
                      required
                      fullWidth
                      select
                      disabled={
                        isLoading ||
                        (mode === 'edit' &&
                          productForm.category === 'Subscription')
                      }
                      value={category}
                      onChange={handleChange}
                      onKeyUp={dropdownKeyEventHandler(
                        productCategoryOptions,
                        (option) =>
                          setProductForm({ ...productForm, category: option }),
                      )}
                    >
                      {productCategoryOptions.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  {productForm.category === 'Subscription' ? (
                    <>
                      <Grid item xs={12}>
                        {mode === 'create' ? (
                          <TextField
                            id="priceId"
                            label="Stripe Price ID"
                            name="priceId"
                            required
                            fullWidth
                            disabled={isLoading}
                            value={priceId}
                            onChange={handleChange}
                          />
                        ) : (
                          <Typography>Stripe Price ID: {priceId}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        {' '}
                        <TextField
                          id="downgradeId"
                          label="Stripe Downgrade Price ID"
                          name="downgradeId"
                          required
                          fullWidth
                          disabled={isLoading}
                          value={downgradeId}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          id="upgradeId"
                          label="Stripe Upgrade Price ID"
                          name="upgradeId"
                          required
                          fullWidth
                          disabled={isLoading}
                          value={upgradeId}
                          onChange={handleChange}
                        />
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <TextField
                        id="fullPrice"
                        label="Full Price"
                        name="fullPrice"
                        type="number"
                        required
                        fullWidth
                        disabled={isLoading}
                        value={fullPrice ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (Number.isNaN(e.target.value)) {
                            setProductForm({ ...productForm, fullPrice: '' });
                            return;
                          }
                          setProductForm({
                            ...productForm,
                            fullPrice: e.target.valueAsNumber,
                          });
                        }}
                        InputProps={{
                          inputProps: { min: 0 },
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item>
                    <FormControlLabel
                      label="Hidden"
                      disabled={isLoading}
                      control={
                        <Checkbox
                          checked={!!hidden}
                          onChange={(e) => {
                            setProductForm({
                              ...productForm,
                              hidden: e.target.checked,
                            });
                          }}
                        />
                      }
                    />
                  </Grid>
                  {category !== 'Subscription' && (
                    <>
                      <Grid item>
                        <FormControlLabel
                          label="On Sale"
                          disabled={isLoading}
                          control={
                            <Checkbox
                              checked={!!sale}
                              onChange={(e) => {
                                if (!e.target.checked) {
                                  clearSale();
                                  return;
                                }
                                editSale({ salePrice: '', until: null });
                              }}
                            />
                          }
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          id="salePrice"
                          label="Sale Price ($)"
                          name="salePrice"
                          type="number"
                          disabled={isLoading || !sale}
                          value={salePrice ?? ''}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            if (Number.isNaN(e.target.valueAsNumber)) {
                              editSale({ salePrice: '', until: until ?? null });
                              return;
                            }
                            editSale({
                              salePrice: e.target.valueAsNumber,
                              until: until ?? null,
                            });
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <DatePicker
                          label="Sale Ends On"
                          name="until"
                          disabled={isLoading || !sale}
                          value={dayjs(until)}
                          onChange={(end) => {
                            editSale({
                              salePrice: salePrice ?? '',
                              until: end ? end.toISOString() : '',
                            });
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} sx={{ pt: 0 }}>
                  <TextField
                    id="keywords"
                    label="Product Hover Text"
                    name="keywords"
                    required
                    fullWidth
                    disabled={isLoading}
                    value={keywords}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MarkdownEditor
                    name="Brief Description"
                    id="brief-description"
                    value={briefDescription}
                    handleChange={(value) =>
                      setProductForm({
                        ...productForm,
                        briefDescription: value ?? '',
                      })
                    }
                  />
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={1}
                  >
                    <Typography variant="caption">
                      Min Length: 10, Max Length: 1000
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{
                        color:
                          briefDescription.length < 10
                            ? 'red'
                            : briefDescription.length <= 1000
                            ? 'green'
                            : 'red',
                      }}
                    >
                      {briefDescription.length}/1000
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="supportedPlatforms"
                    label="Supported Platforms"
                    name="supportedPlatforms"
                    required
                    fullWidth
                    select
                    SelectProps={{ multiple: true }}
                    disabled={isLoading}
                    value={supportedPlatforms}
                    onChange={handleChange}
                  >
                    {platformOptions.map(({ label, value }) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <AssignSupportedHostApps
                  supportedHostApps={supportedHostApplications}
                  changeSupportedHostApps={(apps: SupportedHostApps) => {
                    setProductForm({
                      ...productForm,
                      supportedHostApplications: [...apps],
                    });
                  }}
                />
                <Grid item xs={6}>
                  <TextField
                    id="productPage"
                    label="Product Page"
                    name="productPage"
                    required
                    fullWidth
                    disabled={isLoading}
                    value={productPage}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="downloadPage"
                    label="Download Page"
                    name="downloadPage"
                    required
                    fullWidth
                    disabled={isLoading}
                    value={downloadPage}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MarkdownEditor
                    name="Full Description"
                    id="full-description"
                    value={fullDescription}
                    handleChange={(value) =>
                      setProductForm({
                        ...productForm,
                        fullDescription: value ?? '',
                      })
                    }
                  />
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={1}
                  >
                    <Typography variant="caption">
                      Min Length: 100, Max Length: 10000
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{
                        color:
                          fullDescription.length < 100
                            ? 'red'
                            : fullDescription.length <= 10000
                            ? 'green'
                            : 'red',
                      }}
                    >
                      {fullDescription.length}/10000
                    </Typography>
                  </Box>
                </Grid>
                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      Error creating product:{' '}
                      {'status' in error && error.status}
                    </Alert>
                  </Grid>
                )}
                {formError && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      There is an error in this form. Please correct it and try
                      again.
                    </Alert>
                  </Grid>
                )}
                {isSuccess && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      {mode === 'create'
                        ? 'Product created!'
                        : 'Product edited!'}
                    </Alert>
                  </Grid>
                )}
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isLoading}
                    // sx={{ mt: 2 }}
                    onClick={handleSubmit}
                  >
                    {isLoading ? <CircularProgress /> : submitButtonText}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default ProductFormPage;
